import { create } from "zustand";
import {
  CreateJob,
  FileMetadata,
  Job,
  SoftwareSettingsMetadata,
} from "@domain/schemas/BI";
import type { MergeNodePoolOption } from "@domain/valueObjects";
import {
  JobPriorityEnumSchema,
  JobPipelineStepDetail,
  SoftwareEnumSchema,
  JobPipelineTypeSchema,
  SNStepEnumSchema,
} from "@domain/schemas/BI";

const STEP_TYPE_TO_STABLE_ID: Partial<Record<SNStepEnumSchema, string>> = {
  [SNStepEnumSchema.PulsarStep1]: "22222222-2222-2222-2222-222222222222",
  [SNStepEnumSchema.LibraryGeneration]: "55555555-5555-5555-5555-555555555555",
  [SNStepEnumSchema.DiaAnalysis]: "66666666-6666-6666-6666-666666666666",
  [SNStepEnumSchema.SneMerge]: "77777777-7777-7777-7777-777777777777",
};

// ✅ Default state
const defaultCreateJobState: CreateJob = {
  quoteNumber: "",
  name: "",
  analysisName: "",
  software: SoftwareEnumSchema.Spectronaut,
  pipelineType: JobPipelineTypeSchema.DirectDIA, // Default
  priority: JobPriorityEnumSchema.Normal,
  batchSize: 1, // set it automatically to 1 as a default value.

  // fasta
  fastaFiles: [],
  rawFiles: [],
  libraryFiles: [],
  archiveFiles: [],
  softwareSettingsFile: null,

  conditionSetupFile: null,
  modificationRepoFile: null,
  reportSettingsFiles: [] as string[],
  goFiles: [] as string[],
  mergeNodePoolOverride: null,
  pattern: "",
  outputPath: "",
};

// ✅ Store Type
type JobSetupStore = {
  CreateJobState: CreateJob;
  sourceSoftwareVersion: string | null;
  //used to allow the user to choose if he wants to be redirected to the job list after creating the job or not(useful when want to create several jobs)
  redirectToJobList: boolean;
  showValidation: boolean;
  allowCreationButton: boolean;
  rerunAvailableSteps: JobPipelineStepDetail[];

  // Selected settings
  settingsDirectDIA: SoftwareSettingsMetadata | null;
  modificationRepository: SoftwareSettingsMetadata | null;
  conditionSetup: SoftwareSettingsMetadata | null;
  reportSettings: SoftwareSettingsMetadata[] | null;
  goSettings: SoftwareSettingsMetadata[] | null;
  mergeNodePoolOptions: MergeNodePoolOption[];
  mergeNodePoolOptionsLoading: boolean;
  mergeNodePoolOptionsError: string | null;

  handleRedirectToJobListChange: () => void;
  resetCreateJobState: () => void;
  loadExistingJobForRerun: (job: Job) => void;
  setRerunAvailableSteps: (steps: JobPipelineStepDetail[], onlyFirstStep?: boolean) => void;
  setResumeFromStepId: (resumeFromStepId: string | null) => void;

  setQuoteNumber: (quoteNumber: string) => void;
  setName: (name: string) => void;
  setAnalysisName: (analysisName: string) => void;
  setPriority: (priority: JobPriorityEnumSchema) => void;
  setSoftware: (software: SoftwareEnumSchema) => void;
  setSoftwareVersion: (softwareVersion: string) => void;
  setBatchSize: (batchSize: number) => void;
  setJobFastaFiles: (fastaFiles: string[]) => void;
  setJobRawFiles: (rawFiles: FileMetadata[]) => void;
  setJobLibraryFiles: (libraryFiles: FileMetadata[]) => void;
  setJobArchiveFiles: (archiveFiles: FileMetadata[]) => void;
  setPipelineType: (pipelineType: JobPipelineTypeSchema) => void;
  setMergeNodePoolOverride: (mergeNodePoolOverride: string | null) => void;
  setMergeNodePoolOptionsLoading: (loading: boolean) => void;
  setMergeNodePoolOptions: (options: MergeNodePoolOption[]) => void;
  setMergeNodePoolOptionsError: (error: string | null) => void;

  // Settings handlers
  setDirectDIASettings: (settings: SoftwareSettingsMetadata[]) => void;
  setModificationRepository: (settings: SoftwareSettingsMetadata[]) => void;
  setConditionSetup: (settings: SoftwareSettingsMetadata[]) => void;
  setReportSettings: (settings: SoftwareSettingsMetadata[]) => void;
  setGOSettings: (settings: SoftwareSettingsMetadata[]) => void;

  setAllowCreationButton: (allow: boolean) => void;
  setShowValidation: (show: boolean) => void;

  // Right panel tab control (0 = Raw Files, 1 = Libraries)
  rightPanelTab: number;
  setRightPanelTab: (tab: number) => void;
};

// ✅ Implementation
export const useJobSetupStore = create<JobSetupStore>()((set, get) => ({
  CreateJobState: defaultCreateJobState,
  sourceSoftwareVersion: null,
  redirectToJobList: true,
  showValidation: false,
  allowCreationButton: false,
  rerunAvailableSteps: [],

  // Settings
  settingsDirectDIA: null,
  modificationRepository: null,
  conditionSetup: null,
  reportSettings: [],
  goSettings: [],
  mergeNodePoolOptions: [],
  mergeNodePoolOptionsLoading: false,
  mergeNodePoolOptionsError: null,

  handleRedirectToJobListChange: () => {
    set({ redirectToJobList: !get().redirectToJobList });
  },

  resetCreateJobState: () => {
    set({
      CreateJobState: {
        ...defaultCreateJobState,
        archiveFiles: [],
      },
      settingsDirectDIA: null,
      modificationRepository: null,
      conditionSetup: null,
      reportSettings: [],
      goSettings: [],
      sourceSoftwareVersion: null,
      showValidation: false,
      allowCreationButton: false,
      rerunAvailableSteps: [],
      mergeNodePoolOptions: [],
      mergeNodePoolOptionsLoading: false,
      mergeNodePoolOptionsError: null,
      rightPanelTab: 0,
    });
  },

  loadExistingJobForRerun: (job) => {
    const toFileMetadata = (paths?: string[] | null): FileMetadata[] =>
      (paths ?? []).filter(Boolean).map((path) => ({ path, sizeInBytes: 0 }));

    const toSingleSetting = (path?: string | null): SoftwareSettingsMetadata | null => {
      if (!path) return null;

      const normalizedPath = path.replace(/\\/g, "/");
      const segments = normalizedPath.split("/").filter(Boolean);
      const name = segments[segments.length - 1] ?? normalizedPath;
      const location = segments.slice(0, -1).join("/");

      return {
        id: normalizedPath,
        name,
        location,
        createdBy: job.createdBy ?? "",
        software: job.software,
        softwareVersion: job.softwareVersion ?? "",
        settingsType: undefined as never,
      };
    };

    const toManySettings = (paths?: string[] | null): SoftwareSettingsMetadata[] =>
      (paths ?? []).filter(Boolean).map((path) => ({
        id: path,
        name: path.split("/").filter(Boolean).pop() ?? path,
        location: path.split("/").filter(Boolean).slice(0, -1).join("/"),
        createdBy: job.createdBy ?? "",
        software: job.software,
        softwareVersion: job.softwareVersion ?? "",
        settingsType: undefined as never,
      }));

    set({
      CreateJobState: {
        ...defaultCreateJobState,
        quoteNumber: "",
        name: job.analysisName ? (job.name ?? "").replace(new RegExp(`_${job.analysisName}$`), "") : (job.name ?? ""),
        analysisName: job.analysisName ?? "",
        description: job.description ?? "",
        software: job.software,
        softwareSettingsFile: job.softwareSettingsFile ?? null,
        conditionSetupFile: job.conditionSetupFile ?? null,
        modificationRepoFile: job.modificationRepositoryFile ?? null,
        reportSettingsFiles: job.reportSettingsFile ?? [],
        goFiles: job.goFile ?? [],
        rawFiles: toFileMetadata(job.rawFiles),
        fastaFiles: job.fastaFiles ?? [],
        pattern: "",
        outputPath: job.outputPath ?? "",
        softwareVersion: job.softwareVersion ?? "",
        batchSize: 1,
        priority: job.priority,
        pipelineType: job.pipelineType ?? JobPipelineTypeSchema.DirectDIA,
        libraryFiles: toFileMetadata(job.libraryFiles),
        archiveFiles: toFileMetadata(job.archiveFiles),
        mergeNodePoolOverride: job.mergeNodePoolOverride ?? null,
        sourceCreatedAt: job.createdAt ?? null,
        sourceJobName: job.name ?? null,
        sourceAnalysisName: job.analysisName ?? null,
      },
      settingsDirectDIA: toSingleSetting(job.softwareSettingsFile),
      modificationRepository: toSingleSetting(job.modificationRepositoryFile),
      conditionSetup: toSingleSetting(job.conditionSetupFile),
      reportSettings: toManySettings(job.reportSettingsFile),
      goSettings: toManySettings(job.goFile),
      sourceSoftwareVersion: job.softwareVersion ?? null,
      showValidation: false,
      allowCreationButton: true,
      rerunAvailableSteps: [],
      mergeNodePoolOptions: get().mergeNodePoolOptions,
      mergeNodePoolOptionsLoading: false,
      mergeNodePoolOptionsError: get().mergeNodePoolOptionsError,
      rightPanelTab: 0,
    });
  },

  setRerunAvailableSteps: (steps, onlyFirstStep = false) => {
    const allowedTypes = new Set([
      SNStepEnumSchema.PulsarStep1,
      SNStepEnumSchema.LibraryGeneration,
      SNStepEnumSchema.DiaAnalysis,
      SNStepEnumSchema.SneMerge,
    ]);
    const filteredSteps = steps
      .filter((step) => allowedTypes.has(step.type as SNStepEnumSchema))
      .sort((a, b) => a.position - b.position);
    const availableSteps = onlyFirstStep ? filteredSteps.slice(0, 1) : filteredSteps;
    const defaultResumeStep = availableSteps[0] ? STEP_TYPE_TO_STABLE_ID[availableSteps[0].type as SNStepEnumSchema] ?? null : null;

    set((state) => ({
      rerunAvailableSteps: availableSteps,
      CreateJobState: {
        ...state.CreateJobState,
        resumeFromStepId: defaultResumeStep,
      },
    }));
  },

  setResumeFromStepId: (resumeFromStepId) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, resumeFromStepId },
    })),

  setQuoteNumber: (quoteNumber) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, quoteNumber },
    })),

  setName: (name) =>
    set((state) => ({ CreateJobState: { ...state.CreateJobState, name } })),

  setAnalysisName: (analysisName) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, analysisName },
    })),

  setPriority: (priority) =>
    set((state) => ({ CreateJobState: { ...state.CreateJobState, priority } })),

  setSoftware: (software) =>
    set((state) => ({ CreateJobState: { ...state.CreateJobState, software } })),

  setSoftwareVersion: (softwareVersion) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, softwareVersion },
    })),

  setBatchSize: (batchSize) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, batchSize },
    })),

  setJobFastaFiles: (fastaFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, fastaFiles },
    })),

  setJobRawFiles: (rawFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, rawFiles: rawFiles.filter(Boolean) },
    })),

  setJobLibraryFiles: (libraryFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, libraryFiles: libraryFiles.filter(Boolean) },
    })),

  setJobArchiveFiles: (archiveFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, archiveFiles: archiveFiles.filter(Boolean) },
    })),

  setPipelineType: (pipelineType) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, pipelineType },
    })),

  setMergeNodePoolOverride: (mergeNodePoolOverride) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, mergeNodePoolOverride },
    })),

  // ✅ Settings
  setDirectDIASettings: (settings) =>
    set((state) => ({
      settingsDirectDIA: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        softwareSettingsFile: settings[0]
          ? settings[0].location
            ? `${settings[0].location}/${settings[0].name}`
            : settings[0].name
          : null,
      },
    })),

  setModificationRepository: (settings) =>
    set((state) => ({
      modificationRepository: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        modificationRepoFile: settings[0]
          ? settings[0].location
            ? `${settings[0].location}/${settings[0].name}`
            : settings[0].name
          : null,
      },
    })),

  setConditionSetup: (settings) =>
    set((state) => ({
      conditionSetup: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        conditionSetupFile: settings[0]
          ? settings[0].location
            ? `${settings[0].location}/${settings[0].name}`
            : settings[0].name
          : null,
      },
    })),

  setReportSettings: (settings) =>
    set((state) => ({
      reportSettings: settings,
      CreateJobState: {
        ...state.CreateJobState,
        reportSettingsFiles: settings.map(
          (s) => (s.location ? `${s.location}/` : "") + s.name
        ),
      },
    })),

  setGOSettings: (settings) =>
    set((state) => ({
      goSettings: settings,
      CreateJobState: {
        ...state.CreateJobState,
        goFiles: settings.map(
          (s) => (s.location ? `${s.location}/` : "") + s.name
        ),
      },
    })),

  setMergeNodePoolOptionsLoading: (loading) => set({ mergeNodePoolOptionsLoading: loading }),
  setMergeNodePoolOptions: (options) => set({ mergeNodePoolOptions: options, mergeNodePoolOptionsError: null }),
  setMergeNodePoolOptionsError: (error) => set({ mergeNodePoolOptionsError: error }),

  setAllowCreationButton: (allow) => set({ allowCreationButton: allow }),
  setShowValidation: (show) => set({ showValidation: show }),

  rightPanelTab: 0,
  setRightPanelTab: (tab) => set({ rightPanelTab: tab }),
}));
